//REV-21365 — Vokabular des Konto-Bereichs „Kommentare" (abcp-account-notes).
//
//Der Entwurf steht in ADR-0019 (docs/architecture/abcp/adr/), die Begriffe und die
//Prototyp-Belege in docs/architecture/abcp/architecture/kommentare-und-pendenzen.md.
//
//⚠️ „Pendenz" heisst HIER die frei erfasste Aufgabe — nicht die aus den Rule-Packs abgeleiteten
//Zeilen des Arbeitsprogramms (REV-21759). Siehe Glossar.

/** Die beiden Ausprägungen EINES Aggregats (ADR-0019 E1). Nach dem Anlegen unveränderlich. */
export type NoteKind = 'COMMENT' | 'TODO';

/**
 * Woher der Eintrag stammt (ADR-0019 E5).
 *
 * ⚠️ Eigenes Feld, obwohl heute nur `ACCOUNT` schreibt: die Ansicht zeigt die Herkunft trotzdem an,
 * damit eine zweite Quelle eine DATEN- und keine UI-Änderung ist.
 */
export type NoteSource = 'ACCOUNT' | 'DOCUMENT' | 'MANDATE' | 'AGENT';

/** Klassifizierung eines Kommentars. Mindestens eine ist Pflicht. */
export type CommentTag = 'internal' | 'closingMeeting';

/** Zielgruppe einer Pendenz. Mindestens eine ist Pflicht. */
export type TodoTag = 'todo' | 'reviewer' | 'client';

export type NoteTag = CommentTag | TodoTag;

/** Agent oder Person — die einzige Unterscheidung, die der Kopf eines Eintrags zeichnet. */
export interface NoteAuthor {
    type: 'agent' | 'user';
    /** `null` ⇒ die Ansicht zeigt `abcp_notes_author_unknown`. */
    name?: string | null;
    initials?: string | null;
}

/**
 * Eine erwähnte Person, **anzeige-fertig** (ADR-0019 E3).
 *
 * ⚠️ Der Pure View parst keine Token: das Speicherformat `@[Name|userId]` entsteht und zerfällt im
 * Orchestrator. Hier steht nur, was hervorzuheben ist.
 */
export interface NoteMention {
    userId: string;
    displayName: string;
}

/**
 * Die zitierte Stelle (ADR-0019 E5) — zwei Felder, keine Geometrie.
 *
 * ⚠️ Ohne `blockId` (oder, im Beleg, `documentId`) ist der Chip sichtbar, aber NICHT klickbar: ein
 * Klick, der nirgendwohin führt, ist schlimmer als kein Klick.
 */
export interface NoteAnchor {
    /** `''` bei einer Flächen-Markierung im Beleg — sie hat keinen Text; dann ist `page` gesetzt. */
    snippet: string;
    blockId?: string | null;
    /**
     * REV-21369 — die Seite einer Markierung im Beleg (ADR-0021 E5). Die Ansicht zeigt sie nur an;
     * Rechtecke rechnet und zeichnet ausschliesslich der PDF-Viewer.
     */
    page?: number | null;
    /**
     * REV-21369 (ADR-0021 Nachtrag N4) — der Beleg einer Markierung. Gesetzt NUR, wenn die Stelle im
     * Beleg liegt; dann ist der Chip klickbar und führt zur Markierung im Viewer.
     */
    documentId?: string | null;
}

/** REV-21369 (N4) — ein Klick auf das Zitat einer Markierung im Beleg: welche Notiz, welcher Beleg. */
export interface NoteDocumentAnchorSelect {
    noteId: string;
    documentId: string;
}

/** Eine Antwort. Eine Ebene tief — es gibt keine Antwort auf eine Antwort (ADR-0019 E4). */
export interface NoteReply {
    id: string;
    author: NoteAuthor;
    /** Fertig formatiert geliefert; die Ansicht rechnet nichts um. */
    timeLabel: string;
    text: string;
    mentions: NoteMention[];
}

export interface NoteEntry {
    id: string;
    /** Der Bereich listet `COMMENT`; der Typ trägt beide, weil der Verfasser beide erzeugt. */
    kind: NoteKind;
    source: NoteSource;
    author: NoteAuthor;
    timeLabel: string;
    text: string;
    mentions: NoteMention[];
    /** Klassifizierung bzw. Zielgruppe. Genau `['internal']` wird NICHT beschriftet (AC2). */
    tags: NoteTag[];
    anchor?: NoteAnchor | null;
    replies: NoteReply[];
    /** Kommt herein, wird nie aus `replies` gezählt — die Liste kann eine Seite sein. */
    replyCount: number;
    lastReplyTimeLabel?: string | null;
    /** Nur bei `TODO`. */
    done?: boolean;
    /**
     * Rechte je Eintrag (ADR-0019 E9). Ohne Recht ist der Menüpunkt ABWESEND, nicht ausgegraut.
     *
     * ⚠️ Der Pure View entscheidet nichts davon — er liest es am Eintrag. Rechte in einer dummen
     * Komponente zu rechnen hiesse, sie an drei Stellen zu rechnen.
     */
    canEdit: boolean;
    canDelete: boolean;
}

/** Eine markierte Textstelle, die der Host hereinreicht (AC11). */
export interface NoteSelection {
    snippet: string;
    blockId?: string | null;
}

/** Was der Verfasser beim Senden meldet. */
export interface NoteSubmit {
    kind: NoteKind;
    text: string;
    tags: NoteTag[];
    /** Getrennt vom Text — das Speicherformat baut der Orchestrator (ADR-0019 E3). */
    mentionUserIds: string[];
    /** Gesetzt, wenn der Nutzer das Zitat-Kästchen angehakt gelassen hat. */
    anchor?: NoteAnchor | null;
}

/** Was das Speichern einer Bearbeitung meldet. Die Art fehlt — sie ist unveränderlich. */
export interface NoteEditSubmit {
    id: string;
    text: string;
    tags: NoteTag[];
    /** The field's own mentions: a renamed person keeps the name the text still shows. */
    mentions: NoteMention[];
}

export interface NoteReplySubmit {
    noteId: string;
    text: string;
    mentions: NoteMention[];
}

/** Eine Person für die `@`-Vorschlagsliste. Kommt herein — ein Pure View lädt keine Benutzer. */
export interface NoteUser {
    userId: string;
    displayName: string;
    initials?: string | null;
}

/** Ein Stück Text: Fliesstext oder eine Erwähnung. Die Ansicht hebt hervor, sie parst nicht. */
export interface NoteTextSegment {
    text: string;
    mention: boolean;
}

/**
 * Vorbelegung des Verfassers. **`null` am `@Input()` heisst: zu** — dieselbe Form, die
 * `abcp-account-workspace` für `manualEntry` und `reviewDialog` verwendet. The composer writes
 * comments only, so there is nothing to prefill.
 */
export type ComposerPrefill = Record<string, never>;
